/**
 * Feed-row fit signal — PRODUCT INVARIANT, read before changing.
 *
 * lokerdollar is, by definition, a remote-jobs board curated for Indonesian
 * professionals. "Indonesia-friendly" is therefore the BASELINE PROMISE of
 * the whole product — NOT a per-row attribute worth a badge. A green
 * "Ramah Indonesia" / "Indonesia-friendly" chip fired on the ~74% of rows
 * scoring high fit: it carried zero information, competed with the
 * personalized Land Signal ("COCOK"), and quietly implied the other rows are
 * NOT friendly — undermining the brand. It was removed on purpose.
 *
 * The only fit signal worth a row badge is the EXCEPTION: a concrete reason
 * an Indonesian professional probably CANNOT land this specific role
 * (employer hires US-only, requires on-site, demands a hard timezone
 * overlap, …). Rare + specific + protective → it builds trust and saves
 * wasted applications, which is what actually lifts apply-rate.
 *
 * RULE: never add a positive/affirming fit badge to the feed row. Signal
 * value = rarity × specificity. A positive badge on the majority is noise.
 * `tests/component/job-row.test.tsx` and `tests/unit/lib/jobs/fit-blocker.test.ts`
 * guard this — a high-fit row must render NO fit chip.
 *
 * This classifier reads the LLM's own `fit_reason` prose (stored on
 * job_summaries) and returns a short, specific blocker key — or null when
 * the reason is not an eligibility blocker (e.g. "doesn't mention USD pay"
 * is a soft signal, NOT a reason to scare the user off).
 */

export type FitBlocker = "us" | "eu" | "uk" | "canada" | "onsite" | "timezone";

/** i18n key for each blocker (resolved against the dictionary in the UI). */
export const FIT_BLOCKER_LABEL_KEY: Record<FitBlocker, string> = {
	us: "fitBlockerUs",
	eu: "fitBlockerEu",
	uk: "fitBlockerUk",
	canada: "fitBlockerCanada",
	onsite: "fitBlockerOnsite",
	timezone: "fitBlockerTimezone",
};

// Order matters: a hard geo restriction outranks on-site, which outranks a
// timezone overlap. Each pattern requires an explicit eligibility phrase so a
// passing mention ("we have a US office") doesn't trip it.
const RULES: ReadonlyArray<{ kind: FitBlocker; re: RegExp }> = [
	{
		kind: "us",
		re: /\bus[-\s]?based\b|\bu\.s\.?\s?(only|based|applicants)\b|\bunited states\b|\b(us|usa)[-\s]?(only|applicants)\b|\bnyc\b|\bnew york[-\s]?based\b|\bmust be (?:located|based) in the (?:us|united states)\b/i,
	},
	{
		kind: "eu",
		re: /\beu[-\s]?based\b|\b(eu|europe|european union|eea)[-\s]?only\b|\bmust be (?:located|based) in (?:the )?(?:eu|europe)\b/i,
	},
	{
		kind: "uk",
		re: /\buk[-\s]?based\b|\buk[-\s]?only\b|\bunited kingdom\b|\bright to work in the uk\b/i,
	},
	{
		kind: "canada",
		re: /\bcanada[-\s]?based\b|\bcanada only\b|\bcanadian (?:applicants|residents|work)\b/i,
	},
	{
		kind: "onsite",
		re: /\bon-?site\b|\bnot remote\b|\bnon-remote\b|\brelocat(?:e|ion)\b|\bin-office\b/i,
	},
	{
		kind: "timezone",
		re: /\btime[-\s]?zone\b|\bhours? of overlap\b|\boverlap with [a-z]+ hours\b/i,
	},
];

/**
 * Classify the LLM `fit_reason` into a concrete eligibility blocker, or null
 * when it names no hard blocker. Display-only and fail-safe: a miss just
 * means no chip (never a thrown error or a false scary badge).
 */
export function classifyFitBlocker(
	reason: string | null | undefined,
): FitBlocker | null {
	if (!reason) return null;
	for (const { kind, re } of RULES) {
		if (re.test(reason)) return kind;
	}
	return null;
}
